import React, { useEffect, useState } from "react";
import { ViewConfig } from "../types/views";
import { useApi as useSimpleApi } from "../hooks/useSimpleApi";
import { useEventSource } from "../hooks/useEventSource";
import { EventData, SyncEventData } from "../types";

const AdminOverview: React.FC = () => {
  const [views, setViews] = useState<ViewConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    type: "success" | "error" | "info" | null;
    text: string;
  }>({ type: null, text: "" });
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [syncIntervals, setSyncIntervals] = useState<{
    incremental: number;
    full: number;
  }>({ incremental: 300, full: 7200 });
  const [isSyncRunning, setIsSyncRunning] = useState(false);
  const [currentSyncType, setCurrentSyncType] = useState<
    "incremental" | "full" | null
  >(null);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const api = useSimpleApi();

  // SSE Event-Handler für Sync-Updates
  const {} = useEventSource("/api/events", {
    onMessage: (data: EventData) => {
      const event = data as SyncEventData;
      console.log("🔄 Admin SSE Event:", event);

      switch (event.type) {
        case "sync_started":
          setIsSyncRunning(true);
          setCurrentSyncType(event.sync_type || null);
          setSyncMessage({
            type: "info",
            text: `${event.sync_type === "full" ? "Full" : "Incremental"}-Sync läuft...`,
          });
          break;

        case "sync_completed":
          setIsSyncRunning(false);
          setCurrentSyncType(null);
          setSyncMessage({
            type: "success",
            text: `Sync erfolgreich abgeschlossen. ${event.stats?.positions?.processed || 0} Einträge verarbeitet.`,
          });
          setLastSyncTime(new Date().toLocaleString("de-DE"));
          // Reload sync status
          loadSyncStatus();
          // Clear message after 10 seconds
          setTimeout(() => setSyncMessage({ type: null, text: "" }), 10000);
          break;

        case "sync_failed":
          setIsSyncRunning(false);
          setCurrentSyncType(null);
          setSyncMessage({
            type: "error",
            text: `Sync fehlgeschlagen: ${event.error_message || "Unbekannter Fehler"}`,
          });
          // Clear message after 10 seconds
          setTimeout(() => setSyncMessage({ type: null, text: "" }), 10000);
          break;
      }
    },
    onError: (error) => {
      console.error("❌ Admin SSE Error:", error);
    },
  });

  // Formatiert Sekunden in lesbare Zeitangabe
  const formatInterval = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} Sekunde${seconds !== 1 ? "n" : ""}`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} Minute${minutes !== 1 ? "n" : ""}`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      return `${hours} Stunde${hours !== 1 ? "n" : ""}`;
    } else {
      const days = Math.floor(seconds / 86400);
      return `${days} Tag${days !== 1 ? "e" : ""}`;
    }
  };

  const loadSyncStatus = async () => {
    try {
      const response = await api.get("/api/dashboard/sync/status");
      setSyncStatus(response.data);

      // Check if sync is currently running
      if (
        response.data.last_incremental_sync?.status === "started" ||
        response.data.last_full_sync?.status === "started"
      ) {
        setIsSyncRunning(true);
        const runningSyncType =
          response.data.last_incremental_sync?.status === "started"
            ? "incremental"
            : "full";
        setCurrentSyncType(runningSyncType);
      }
    } catch (error) {
      console.error("Failed to load sync status:", error);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      try {
        // Load views
        const viewsResponse = await api.get("/api/views/", {
          signal: abortController.signal,
        });

        // Load sync configuration
        const configResponse = await api.get("/api/status/config", {
          signal: abortController.signal,
        });

        if (!abortController.signal.aborted) {
          setViews(viewsResponse.data);

          // Set sync intervals from config
          if (configResponse.data) {
            setSyncIntervals({
              incremental: configResponse.data.sync_interval_seconds || 300,
              full: configResponse.data.full_sync_interval_seconds || 7200,
            });
          }

          // Load sync status
          await loadSyncStatus();

          setLoading(false);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error("Failed to load data:", error);
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      abortController.abort();
    };
  }, []);

  const handleFullSync = async () => {
    setSyncing(true);
    setSyncMessage({ type: null, text: "" });

    try {
      const response = await api.post("/api/dashboard/sync?sync_type=full");

      if (response.data.status === "started") {
        // Sync started in background - SSE will handle updates
        setIsSyncRunning(true);
        setCurrentSyncType("full");
        setSyncMessage({
          type: "info",
          text: "Full-Sync wurde gestartet...",
        });
      } else if (response.data.status === "already_running") {
        setSyncMessage({
          type: "info",
          text: response.data.message || "Ein Sync läuft bereits.",
        });
      } else {
        setSyncMessage({
          type: "info",
          text: response.data.message || "Sync wurde gestartet.",
        });
      }
    } catch (error: any) {
      setSyncMessage({
        type: "error",
        text:
          error.response?.data?.detail ||
          error.message ||
          "Fehler beim Starten des Sync-Vorgangs.",
      });
    } finally {
      setSyncing(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (isoString: string | null): string => {
    if (!isoString) return "Nie";

    // Parse as UTC if not already marked with timezone
    const dateStr =
      isoString.endsWith("Z") ||
      isoString.includes("+") ||
      isoString.includes("-")
        ? isoString
        : isoString + "Z";

    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);

    if (diffSeconds < 60) {
      return "Gerade eben";
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      return `vor ${minutes} Minute${minutes !== 1 ? "n" : ""}`;
    } else if (diffSeconds < 86400) {
      const hours = Math.floor(diffSeconds / 3600);
      return `vor ${hours} Stunde${hours !== 1 ? "n" : ""}`;
    } else {
      const days = Math.floor(diffSeconds / 86400);
      return `vor ${days} Tag${days !== 1 ? "en" : ""}`;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-gray-600">Lade Konfiguration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Available Views Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Verfügbare Ansichten ({views.length})
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {views.map((view) => (
              <div
                key={view.view_id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{view.name}</h4>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Aktiv
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-3">{view.description}</p>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Route:</span>
                    <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                      {view.route}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">View ID:</span>
                    <span className="font-mono">{view.view_id}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Datenquelle:</span>
                    <span className="font-mono text-xs">
                      {view.data_source}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Aktualisierung:</span>
                    <span>{view.refresh_interval / 1000}s</span>
                  </div>

                  {view.filters && Object.keys(view.filters).length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      <span className="text-gray-500">Filter:</span>
                      <div className="mt-1 space-y-1">
                        {Object.entries(view.filters).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex items-center justify-between"
                          >
                            <span className="text-gray-400">{key}:</span>
                            <span className="font-mono text-xs bg-yellow-50 px-1 rounded">
                              {Array.isArray(value) ? value.join(", ") : value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t">
                  <a
                    href={view.route}
                    className="block w-full text-center py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Ansicht öffnen →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sync Control Section */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Sync-Steuerung</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {/* Sync Button and Status */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  Manueller Full-Sync
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  Startet eine vollständige Synchronisation aller Daten aus
                  SharePoint
                </p>
                {lastSyncTime && (
                  <p className="text-xs text-gray-400 mt-1">
                    Letzter manueller Sync: {lastSyncTime}
                  </p>
                )}
              </div>
              <button
                onClick={handleFullSync}
                disabled={syncing || isSyncRunning}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  syncing || isSyncRunning
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {isSyncRunning && currentSyncType === "full" ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Full-Sync läuft...
                  </span>
                ) : isSyncRunning ? (
                  <span>Warte auf laufenden Sync...</span>
                ) : (
                  "Full-Sync starten"
                )}
              </button>
            </div>

            {/* Sync Message */}
            {syncMessage.type && (
              <div
                className={`rounded-md p-4 ${
                  syncMessage.type === "success"
                    ? "bg-green-50 border border-green-200"
                    : syncMessage.type === "error"
                      ? "bg-red-50 border border-red-200"
                      : "bg-blue-50 border border-blue-200"
                }`}
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    {syncMessage.type === "success" && (
                      <svg
                        className="h-5 w-5 text-green-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {syncMessage.type === "error" && (
                      <svg
                        className="h-5 w-5 text-red-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {syncMessage.type === "info" && (
                      <svg
                        className="h-5 w-5 text-blue-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p
                      className={`text-sm font-medium ${
                        syncMessage.type === "success"
                          ? "text-green-800"
                          : syncMessage.type === "error"
                            ? "text-red-800"
                            : "text-blue-800"
                      }`}
                    >
                      {syncMessage.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Sync Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">
                Automatische Synchronisation
              </h5>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="space-y-1">
                  <div>
                    <dt className="text-gray-500">Incremental Sync:</dt>
                    <dd className="text-gray-900">
                      Alle {formatInterval(syncIntervals.incremental)}
                    </dd>
                  </div>
                  {syncStatus?.last_incremental_sync?.completed_at && (
                    <dd className="text-xs text-gray-500">
                      Letzter:{" "}
                      {formatRelativeTime(
                        syncStatus.last_incremental_sync.completed_at,
                      )}
                    </dd>
                  )}
                </div>
                <div className="space-y-1">
                  <div>
                    <dt className="text-gray-500">Full Sync:</dt>
                    <dd className="text-gray-900">
                      Alle {formatInterval(syncIntervals.full)}
                    </dd>
                  </div>
                  {syncStatus?.last_full_sync?.completed_at && (
                    <dd className="text-xs text-gray-500">
                      Letzter:{" "}
                      {formatRelativeTime(
                        syncStatus.last_full_sync.completed_at,
                      )}
                    </dd>
                  )}
                </div>
              </dl>
              {isSyncRunning && currentSyncType && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center text-sm text-blue-600">
                    <svg
                      className="animate-spin mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {currentSyncType === "full"
                      ? "Full-Sync"
                      : "Incremental-Sync"}{" "}
                    läuft gerade...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            System Information
          </h3>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Backend API</dt>
              <dd className="mt-1 text-sm text-gray-900">{`http://${window.location.hostname}:8000`}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Frontend</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {window.location.origin}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Konfigurationsdatei
              </dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">
                config/views.json
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Anzahl Views
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{views.length}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
